import { useCallback, useEffect, useRef, useState } from "react";

export type RTEPersistenceStatusOpts = "idle" | "loading" | "saving" | "saved" | "error";

export interface RTEPersistenceOpts {
  readonly load?: () => Promise<string | null>;
  readonly save?: (serialized: string) => Promise<void>;
  readonly saveDebounceMs?: number;
  readonly initialValue?: string | null;
}

export interface RichTextEditorPersistence {
  readonly value: string | null;
  readonly onChange: (serialized: string | null) => void;
  readonly status: RTEPersistenceStatusOpts;
  readonly error: Error | null;
  readonly loading: boolean;
}

const DEFAULT_SAVE_DEBOUNCE_MS = 600;

const toError = (cause: unknown): Error => {
  return cause instanceof Error ? cause : new Error(String(cause));
};

export const useRichTextEditorPersistence = (options: RTEPersistenceOpts): RichTextEditorPersistence => {
  const { load, save, saveDebounceMs = DEFAULT_SAVE_DEBOUNCE_MS, initialValue = null } = options;
  const [value, setValue] = useState<string | null>(initialValue);
  const [status, setStatus] = useState<RTEPersistenceStatusOpts>(load ? "loading" : "idle");
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadRef = useRef(load);
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const loader = loadRef.current;
    if (!loader) return;
    setStatus("loading");
    setError(null);
    loader()
      .then((loaded) => {
        if (!mountedRef.current) return;
        setValue(loaded);
        setStatus("idle");
      })
      .catch((cause: unknown) => {
        if (!mountedRef.current) return;
        setError(toError(cause));
        setStatus("error");
      });
  }, []);

  const runSave = useCallback((next: string) => {
    const saver = saveRef.current;
    if (!saver) return;
    setStatus("saving");
    setError(null);
    saver(next)
      .then(() => {
        if (mountedRef.current) setStatus("saved");
      })
      .catch((cause: unknown) => {
        if (!mountedRef.current) return;
        setError(toError(cause));
        setStatus("error");
      });
  }, []);

  const onChange = useCallback(
    (serialized: string | null) => {
      setValue(serialized);
      if (!saveRef.current || serialized === null) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => runSave(serialized), saveDebounceMs);
    },
    [runSave, saveDebounceMs],
  );

  return { value, onChange, status, error, loading: status === "loading" };
};
